import { Routes } from '@angular/router';

import { authGuard } from './core/guards/auth.guard';
import { dashboardGuard } from './core/guards/dashboard.guard';
import { guestGuard } from './core/guards/guest.guard';
import { userEditGuard } from './core/guards/user-edit.guard';
import { provideAccountFeature } from './features/account/account.module';
import { provideConfigurationsFeature } from './features/configurations/configurations.module';
import { provideDashboardFeature } from './features/dashboard/dashboard.module';
import { provideDocumentVerificationFeature } from './features/document-verification/document-verification.module';
import { providePackageManagementFeature } from './features/package-management/package-management.module';
import { providePartnerManagementFeature } from './features/partner-management/partner-management.module';
import { provideVendorManagementFeature } from './features/vendor-management/vendor-management.module';
import { CLIENT_MANAGEMENT_ROUTES } from './features/client-management/client-management.routes';
import { ROLE_MANAGEMENT_ROUTES } from './features/role-management/role-management.routes';
import { SERVICE_MANAGEMENT_ROUTES } from './features/service-management/service-management.routes';
import { SERVICE_REQUEST_ROUTES } from './features/service-request/service-request.routes';
import { provideUserManagementFeature } from './features/user-role-management/user-management.module';
import { provideHelpSupportTicketsFeature } from './features/help-support-tickets/help-support-tickets.module';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./features/auth/pages/login/login').then((m) => m.Login),
    canActivate: [guestGuard],
  },
  {
    path: '',
    loadComponent: () =>
      import('./shared/components/app-layout/app-layout').then((m) => m.AppLayout),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        providers: [provideDashboardFeature()],
        canActivate: [dashboardGuard],
        loadComponent: () => import('./features/dashboard/pages/home/home').then((m) => m.Home),
        data: {
          layout: {
            activeItem: 'Dashboard',
            breadcrumb: 'Dashboard',
            title: 'Dashboard',
            subtitle: 'Superadmin-level operational overview',
            showSearch: false,
            showFilter: true,
            showCustomizeDashboard: true,
            showDashboardLayoutSelector: true,
          },
        },
      },
      {
        path: 'dashboard/customize',
        loadChildren: () =>
          import('./features/dashboard/pages/customize/dashboard-customize.routes').then(
            (m) => m.DASHBOARD_CUSTOMIZE_ROUTES,
          ),
      },
      {
        path: 'dashboard/manage',
        canActivate: [dashboardGuard],
        loadComponent: () =>
          import('./features/dashboard/pages/manage/dashboard-manage').then(
            (m) => m.DashboardManage,
          ),
        data: {
          layout: {
            activeItem: 'Dashboard',
            breadcrumb: 'Dashboard > Manage Dashboards',
            title: 'Manage Dashboards',
            subtitle: 'Edit or delete your customized dashboards.',
            showToolbar: false,
            backTo: '/',
          },
        },
      },
      {
        path: 'my-account',
        providers: [provideAccountFeature()],
        loadComponent: () =>
          import('./features/account/pages/user-profile/user-profile').then((m) => m.UserProfile),
        data: {
          layout: {
            title: 'My Account',
            subtitle: 'Manage your profile and account actions',
            showToolbar: false,
          },
        },
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/notification/pages/notifications/notifications').then(
            (m) => m.Notifications,
          ),
        data: {
          layout: {
            title: 'Notifications',
            subtitle: 'Stay updated with the latest alerts and activities.',
            searchPlaceholder: 'Search by title or message...',
          },
        },
      },
      {
        path: 'notifications/preferences',
        loadComponent: () =>
          import('./features/notification/pages/notification-preferences/notification-preferences').then(
            (m) => m.NotificationPreferences,
          ),
        data: {
          layout: {
            title: 'Notification Preferences',
            subtitle: 'Choose how and where you receive notifications.',
            showToolbar: false,
            backTo: '/notifications',
          },
        },
      },
      ...SERVICE_REQUEST_ROUTES,
      ...SERVICE_MANAGEMENT_ROUTES,
      {
        path: 'package-management',
        loadComponent: () =>
          import('./features/package-management/pages/package-management/package-management').then(
            (m) => m.PackageManagement,
          ),
        providers: [providePackageManagementFeature()],
        data: {
          layout: {
            activeItem: 'Package Management',
            breadcrumb: 'List of Packages',
            title: 'Package Management',
            subtitle: 'Create and manage subscription-based service packages',
            searchPlaceholder: 'Search by Package Name...',
            showFilter: true,
            showMobileFilterOnly: true,
          },
        },
      },
      {
        path: 'vendor-management/:vendorId',
        providers: [provideVendorManagementFeature()],
        loadComponent: () =>
          import('./features/vendor-management/pages/vendor-detail/vendor-detail').then(
            (m) => m.VendorDetail,
          ),
        data: {
          layout: {
            activeItem: 'Vendor Management',
            breadcrumb: 'List of Vendors > Vendor Details',
            title: 'Vendor Details',
            subtitle: 'View vendor details and partner network',
            showToolbar: false,
            backTo: '/vendor-management',
          },
        },
      },
      {
        path: 'vendor-management',
        providers: [provideVendorManagementFeature()],
        loadComponent: () =>
          import('./features/vendor-management/pages/vendor-management/vendor-management').then(
            (m) => m.VendorManagement,
          ),
        data: {
          layout: {
            activeItem: 'Vendor Management',
            breadcrumb: 'List of Vendors',
            title: 'Vendor Management',
            subtitle: 'Manage vendor management and their partner networks',
            searchPlaceholder: 'Search by Vendor ID/Name...',
            showFilter: true,
            showMobileFilterOnly: true,
          },
        },
      },
      {
        path: 'partner-management/:partnerId',
        providers: [providePartnerManagementFeature()],
        loadComponent: () =>
          import('./features/partner-management/pages/partner-detail/partner-detail').then(
            (m) => m.PartnerDetail,
          ),
        data: {
          layout: {
            activeItem: 'Partner Management',
            breadcrumb: 'List of Partners > Partner Details',
            title: 'Partner Details (Full lifecycle tracking)',
            subtitle: 'View partner details with real-time tracking',
            showToolbar: false,
            backTo: '/partner-management',
          },
        },
      },
      {
        path: 'partner-management',
        providers: [providePartnerManagementFeature()],
        loadComponent: () =>
          import('./features/partner-management/pages/partner-management/partner-management').then(
            (m) => m.PartnerManagement,
          ),
        data: {
          layout: {
            activeItem: 'Partner Management',
            breadcrumb: 'List of Partners',
            title: 'Partner Management',
            subtitle: 'Manage all partners and their schedules',
            searchPlaceholder: 'Search by Partner Name/Phone...',
            showFilter: true,
            showMobileFilterOnly: true,
          },
        },
      },
      ...CLIENT_MANAGEMENT_ROUTES,
      {
        path: 'configurations',
        providers: [provideConfigurationsFeature()],
        loadComponent: () =>
          import('./features/configurations/pages/configurations/configurations').then(
            (m) => m.Configurations,
          ),
        data: {
          layout: {
            activeItem: 'Configurations',
            title: 'Configurations',
            subtitle: 'Configure service level agreement and turnaround times',
            showToolbar: false,
          },
        },
      },
      {
        path: 'documents-verification',
        providers: [provideDocumentVerificationFeature()],
        loadComponent: () =>
          import('./features/document-verification/pages/document-verification/document-verification').then(
            (m) => m.DocumentVerification,
          ),
        data: {
          layout: {
            activeItem: 'Documents Verification',
            breadcrumb: 'List of Documents',
            title: 'Document Verification',
            subtitle: 'Review and verify partner documentation',
            searchPlaceholder: 'Search by Partner name...',
            showFilter: true,
            showMobileFilterOnly: true,
          },
        },
      },
      {
        path: 'user-role-management/users/new',
        providers: [provideUserManagementFeature()],
        loadComponent: () =>
          import('./features/user-role-management/pages/create-user/create-user').then(
            (m) => m.CreateUser,
          ),
        data: {
          layout: {
            activeItem: 'User & Role Management',
            breadcrumb: 'User & Role Management > Create User',
            title: 'Create User',
            subtitle: 'Add user details and assign roles',
            showToolbar: false,
            backTo: '/user-role-management?section=users',
          },
        },
      },
      {
        path: 'user-role-management/users/:userId/edit',
        canActivate: [userEditGuard],
        providers: [provideUserManagementFeature()],
        loadComponent: () =>
          import('./features/user-role-management/pages/create-user/create-user').then(
            (m) => m.CreateUser,
          ),
        data: {
          layout: {
            activeItem: 'User & Role Management',
            breadcrumb: 'User & Role Management > Edit User',
            title: 'Edit User',
            subtitle: 'Update user details, roles and additional policies',
            showToolbar: false,
            backTo: '/user-role-management?section=users',
          },
        },
      },
      {
        path: 'user-role-management/users/:userId',
        providers: [provideUserManagementFeature()],
        loadComponent: () =>
          import('./features/user-role-management/pages/user-detail/user-detail').then(
            (m) => m.UserDetail,
          ),
        data: {
          layout: {
            activeItem: 'User & Role Management',
            breadcrumb: 'User & Role Management > User Details',
            title: 'User Details',
            subtitle: 'View user details, assigned roles and additional policies',
            showToolbar: false,
            backTo: '/user-role-management?section=users',
          },
        },
      },
      {
        path: 'user-role-management/policies/:policyId',
        providers: [provideUserManagementFeature()],
        loadComponent: () =>
          import('./features/user-role-management/pages/policy-detail/policy-detail').then(
            (m) => m.PolicyDetail,
          ),
        data: {
          layout: {
            activeItem: 'User & Role Management',
            breadcrumb: 'User & Role Management > Policy Details',
            title: 'Policy Details',
            subtitle: 'View policy access configuration',
            showToolbar: false,
            backTo: '/user-role-management?section=policies',
          },
        },
      },
      {
        path: 'user-role-management',
        providers: [provideUserManagementFeature()],
        loadComponent: () =>
          import('./features/user-role-management/pages/user-role-management/user-role-management').then(
            (m) => m.UserRoleManagement,
          ),
        data: {
          layout: {
            activeItem: 'User & Role Management',
            breadcrumb: 'User Management',
            title: 'User Management',
            subtitle: 'Manage policies and access controls for users and roles',
            searchPlaceholder: 'Search by Policy Name/Description...',
            showFilter: true,
            showMobileFilterOnly: true,
          },
        },
      },
      {
        path: 'help-support-tickets/settings',
        providers: [provideHelpSupportTicketsFeature()],
        loadComponent: () =>
          import('./features/help-support-tickets/pages/ticket-settings/ticket-settings').then(
            (m) => m.TicketSettings,
          ),
        data: {
          layout: {
            activeItem: 'Help & Support',
            breadcrumb: 'Help & Support Tickets > Ticket Settings',
            title: 'Categories',
            subtitle: 'Keep intake categories and routing rules simple and central.',
            showToolbar: false,
            backTo: '/help-support-tickets',
          },
        },
      },
      {
        path: 'help-support-tickets/:ticketId',
        providers: [provideHelpSupportTicketsFeature()],
        loadComponent: () =>
          import('./features/help-support-tickets/pages/ticket-detail/ticket-detail').then(
            (m) => m.TicketDetail,
          ),
        data: {
          layout: {
            activeItem: 'Help & Support',
            breadcrumb: 'Help & Support Tickets > Ticket Details',
            title: 'Ticket Details',
            subtitle: 'View ticket information, conversation, attachments and activity.',
            showToolbar: false,
            backTo: '/help-support-tickets',
          },
        },
      },
      {
        path: 'help-support-tickets',
        providers: [provideHelpSupportTicketsFeature()],
        loadComponent: () =>
          import('./features/help-support-tickets/pages/help-support-tickets-list/help-support-tickets-list').then(
            (m) => m.HelpSupportTicketsList,
          ),
        data: {
          layout: {
            activeItem: 'Help & Support',
            breadcrumb: 'List of Tickets',
            title: 'Help & Support Tickets',
            subtitle: 'Manage concerns raised through Client and Partner apps.',
            searchPlaceholder: 'Search by ticket ID, user or subject...',
            showFilter: true,
            showMobileFilterOnly: true,
          },
        },
      },
      ...ROLE_MANAGEMENT_ROUTES,
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
